import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Lock, BookOpen, Bookmark, CheckCircle2, ClipboardList } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reader } from "@/components/Reader";
import { CoverImage } from "@/components/CoverImage";
import { SaveLevelButton } from "@/components/SaveLevelButton";
import { savedBookIds, useOnline } from "@/lib/offline";
import { pendingProgress, onOutboxChange } from "@/lib/outbox";
import { InstallButton } from "@/components/InstallButton";
import { MaskLines } from "@/components/Motion";
import { StarsBar } from "@/components/badges/StarsBar";
import { SubscribePanel } from "@/components/SubscribePanel";
import { RenewReminder } from "@/components/RenewReminder";
import { api, SUBJECTS, LEVELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const Status = ({ user }) => {
  const { t } = useLang();
  if (user === null) return null;
  if (!user) return (
    <div className="flex items-center gap-3 bg-white border border-[#E5DCCB] p-4 text-sm" data-testid="library-guest-notice">
      <Lock className="w-5 h-5 text-[#8B3A2B] shrink-0" />
      <span>{t.lib.guest} <Link to="/register" className="font-semibold text-[#8B3A2B] underline">{t.auth.createOne}</Link> {t.lib.or} <Link to="/login" className="font-semibold text-[#8B3A2B] underline">{t.auth.signIn}</Link>.</span>
    </div>
  );
  if (user.role === "user" && !user.has_access && !(user.subscription_until > new Date().toISOString())) return <SubscribePanel />;
  if (!user.has_access) return (
    <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900" data-testid="pending-activation-notice">
      <Clock className="w-5 h-5 shrink-0" />
      <span>{t.lib.pending(user.name)}</span>
    </div>
  );
  if (user.role === "user" && user.subscription_until) return <RenewReminder until={user.subscription_until} />;
  return null;
};

const Tasks = ({ items, onOpen }) => {
  const { t } = useLang();
  if (!items.length) return null;
  const label = { finished: t.lib.finished, in_progress: t.lib.inProgress, not_started: t.lib.notStarted };
  const cls = { finished: "bg-emerald-100 text-emerald-900", in_progress: "bg-amber-100 text-amber-900", not_started: "bg-stone-100 text-stone-700" };
  return (
    <section className="mt-8 bg-white border border-[#E5DCCB] p-5" data-testid="student-tasks">
      <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#047857]" /> {t.lib.tasks}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((a) => (
          <button key={a.id} onClick={() => onOpen(a.book_id)} data-testid={`student-task-${a.id}`}
            className="text-start border border-[#E5DCCB] hover:border-[#1C1917] p-4 transition-colors">
            <div className="flex items-start justify-between gap-2">
              <span className="font-semibold" dir="auto">{a.title}</span>
              <span className={`text-xs rounded-full px-2.5 py-1 shrink-0 ${cls[a.status]}`}>{label[a.status]}</span>
            </div>
            <p className="text-xs text-stone-600 mt-1">{t.subjects[a.subject]?.name} · {t.lib.level} {a.level}{a.due_date ? ` · ${t.lib.due}: ${a.due_date}` : ""}</p>
            {a.note && <p className="text-sm mt-2" dir="auto">{a.note}</p>}
          </button>
        ))}
      </div>
    </section>
  );
};

const ProgressBadges = ({ p, total }) => {
  const { t } = useLang();
  return (
  <div className="absolute top-12 inset-x-5 flex flex-col items-start gap-2">
    {p.finished && (
      <span className="inline-flex items-center gap-1 text-xs font-bold bg-[#1C1917] text-emerald-300 rounded-full px-3 py-1 shadow" data-testid="book-finished-badge">
        <CheckCircle2 className="w-3.5 h-3.5" /> {t.lib.finished}
      </span>
    )}
    <span className="inline-flex items-center gap-1 text-xs font-bold bg-[#FAF7F2] text-[#1C1917] rounded-full px-3 py-1 shadow" data-testid="book-bookmark-badge">
      <Bookmark className="w-3.5 h-3.5 fill-[#B45309] text-[#B45309]" /> {t.lib.pageOf(p.page + 1, total)}
    </span>
  </div>
  );
};

const BookCard = ({ b, onOpen, i, p }) => {
  const { t } = useLang();
  const [coverOk, setCoverOk] = useState(b.type === "pdf");
  return (
  <motion.button layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
    whileHover={{ y: -8, rotate: -1 }} onClick={() => onOpen(b.id)} data-testid={`book-card-${b.id}`}
    className="group text-start">
    <div className={`relative aspect-[4/3] rounded-sm overflow-hidden ${coverOk ? "bg-white ring-1 ring-[#E5DCCB]" : "bg-[#3B2F25]"} shadow-[0_20px_40px_-20px_rgba(28,25,23,0.6)]`}>
      {coverOk && <CoverImage src={`${api.defaults.baseURL}/books/${b.id}/cover?v=2`} alt={b.title} onError={() => setCoverOk(false)} testid={`book-cover-${b.id}`} />}
      <div className="absolute inset-y-0 start-0 w-2 bg-gradient-to-l from-transparent to-black/15" />
      {p && <span className="absolute top-0 end-5 w-6 h-10 bg-[#B45309] [clip-path:polygon(0_0,100%_0,100%_100%,50%_75%,0_100%)] z-10" data-testid={`book-ribbon-${b.id}`} />}
      <div className="absolute inset-0 p-3 flex flex-col justify-between text-[#FAF7F2]">
        {!coverOk ? (
          <span className="self-start text-[11px] font-bold text-amber-100 bg-black/60 rounded-full px-2.5 py-0.5">
            {t.lib.level} {b.level}
          </span>
        ) : <span />}
        {!coverOk && (
          <div dir={b.dir}>
            <h3 className={`text-2xl font-bold leading-tight ${b.dir === "rtl" ? "font-display" : "font-latin"}`}>{b.title}</h3>
          </div>
        )}
      </div>
      {p && <div className="z-10"><ProgressBadges p={p} total={b.page_count} /></div>}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors grid place-items-center">
        <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 bg-[#FAF7F2] text-[#1C1917] text-sm font-semibold rounded-full px-4 py-2"><BookOpen className="w-4 h-4" /> {t.lib.read}</span>
      </div>
    </div>
    {b.title && <p className="mt-3 font-semibold text-[#1C1917] line-clamp-2" dir="auto" data-testid={`book-title-${b.id}`}>{b.title}</p>}
    <p className={`${b.title ? "mt-1" : "mt-3"} text-sm text-stone-600`}>{b.page_count} {t.lib.pages} · {t.subjects[b.subject]?.name}</p>
    {p && (
      <div className="mt-2 h-1 bg-[#E5DCCB] rounded-full overflow-hidden">
        <div className={`h-full ${p.finished ? "bg-[#047857]" : "bg-[#B45309]"}`} style={{ width: `${p.finished ? 100 : ((p.page + 1) / b.page_count) * 100}%` }} />
      </div>
    )}
  </motion.button>
  );
};

export default function Library() {
  const [params, setParams] = useSearchParams();
  const subject = SUBJECTS.some((s) => s.id === params.get("subject")) ? params.get("subject") : "arabic";
  const [level, setLevel] = useState(0);
  const [books, setBooks] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [progress, setProgress] = useState({});
  const [tasks, setTasks] = useState([]);
  const { user } = useAuth();
  const { t } = useLang();
  const canRead = Boolean(user?.has_access);
  const online = useOnline();
  const [savedIds, setSavedIds] = useState(() => new Set());
  const refreshSaved = () => { savedBookIds().then(setSavedIds).catch(() => {}); };
  useEffect(() => { refreshSaved(); }, []);
  const myLevelBooks = useMemo(() => books.filter((b) => b.type === "pdf" && b.level === user?.level), [books, user?.level]);

  useEffect(() => {
    if (!canRead) { setProgress({}); return undefined; }
    // Pages read offline (not yet sent) are newer than what the server knows.
    const loadProgress = async () => {
      const server = await api.get("/progress").then((r) => (r.data && typeof r.data === "object" ? r.data : {})).catch(() => ({}));
      const local = await pendingProgress().catch(() => ({}));
      setProgress({ ...server, ...local });
    };
    loadProgress();
    api.get("/my/assignments").then((r) => setTasks(Array.isArray(r.data) ? r.data : [])).catch(() => setTasks([]));
    return onOutboxChange(loadProgress);
  }, [canRead, openId]);

  const updateProgress = (id, p) => setProgress((prev) => ({ ...prev, [id]: p }));

  useEffect(() => {
    api.get("/books").then((r) => setBooks(Array.isArray(r.data) ? r.data : [])).catch(() => setBooks([]));
  }, []);


  const filtered = useMemo(() => books.filter((b) => b.subject === subject && (!level || b.level === level) && (online || savedIds.has(b.id))), [books, subject, level, online, savedIds]);

  return (
    <div data-testid="library-page">
      <Navbar />
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-14 pb-24 min-h-[70vh]">
        <p className="text-sm font-semibold text-[#B45309] mb-3">{t.lib.eyebrow}</p>
        <h1 className="font-display text-5xl sm:text-6xl font-bold mb-8"><MaskLines key={t.lib.title} lines={[t.lib.title]} delay={0.1} /></h1>
        <Status user={user} />
        {canRead && user?.role === "user" && <StarsBar />}
        <Tasks items={tasks} onOpen={setOpenId} />
        {!online && <p className="mt-6 rounded-full bg-amber-100 text-[#7C2D12] px-5 py-3 text-sm font-semibold" data-testid="library-offline-banner">{t.lib.offline}</p>}
        {canRead && user?.role === "user" && <SaveLevelButton books={myLevelBooks} savedIds={savedIds} onSaved={refreshSaved} online={online} />}
        <div className="mt-4"><InstallButton /></div>

        <div className="mt-10 grid grid-cols-2 gap-3 max-w-2xl" data-testid="subject-tabs">
          {SUBJECTS.map((s) => {
            const on = subject === s.id;
            return (
              <button key={s.id} onClick={() => setParams({ subject: s.id })} data-testid={`subject-tab-${s.id}`} aria-pressed={on}
                style={{ borderColor: s.tone, backgroundColor: on ? s.tone : "#FFFFFF", color: on ? "#FFFFFF" : s.tone }}
                className={`relative overflow-hidden text-start rounded-sm border-2 px-4 py-3 sm:px-5 sm:py-4 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 ${on ? "shadow-[0_12px_24px_-14px_rgba(0,0,0,0.6)]" : "hover:shadow-md"}`}>
                <span className={`absolute -bottom-4 end-2 text-6xl leading-none select-none ${on ? "opacity-20" : "opacity-10"} ${s.id === "arabic" ? "font-display" : "font-latin"}`}>{s.glyph}</span>
                <span className="relative block text-lg sm:text-xl font-bold">{t.subjects[s.id].tab}</span>
                {s.native !== t.subjects[s.id].name && <span className="relative block text-xs sm:text-sm opacity-90" dir="auto">{s.native}</span>}
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="level-filters">
          {[0, ...LEVELS.map((l) => l.n)].map((n) => (
            <button key={n} onClick={() => setLevel(n)} data-testid={`level-filter-${n}`}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${level === n ? "bg-[#1C1917] text-[#FAF7F2] border-[#1C1917]" : "border-[#E5DCCB] bg-white hover:border-[#1C1917]"}`}>
              {n ? `${n} · ${t.levels[n]}` : t.lib.all}
            </button>
          ))}
        </div>

        {t.subjects[subject]?.desc && <p className="mt-8 text-stone-600" data-testid="library-subject-desc">{t.subjects[subject].desc}</p>}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="books-grid">
          {filtered.map((b, i) => <BookCard key={b.id} b={b} i={i} onOpen={setOpenId} p={progress[b.id]} />)}
        </div>
        {!filtered.length && <p className="mt-10 text-stone-600" data-testid="library-empty">{t.lib.empty}</p>}
      </section>
      <Footer />
      {openId && <Reader bookId={openId} saved={progress[openId]} onProgress={updateProgress} onClose={() => setOpenId(null)} />}
    </div>
  );
}
